import React from 'react';

import { useLocation } from 'react-router-dom';

import { useAppSelector, useAuthentication } from '@pkg/hooks';

import { DEP_YATIVERSE } from '@app/routes';

import { NavButton } from '../../NavButton';
import { AdminSection } from '../AdminSection';
import { ProfileSection } from '../ProfileSection';
import { SettingsSection } from '../SettingsSection';
import { WishlistsSection } from '../WishlistsSection';
import { YatFantasySection } from '../YatFantasySection';
import { YativerseSection } from '../YativerseSection';

import { NavPopover } from './NavPopover';

import { BottomButtons, TopButtons, Wrapper } from './styles';

interface SectionProps {
    isAuthenticated?: boolean;
}

export const MinimizedSection = ({ isAuthenticated }: SectionProps) => {
    const location = useLocation();
    const { handleAuthenticate } = useAuthentication();
    const { showDiscoverPage, yatFantasyEnabled, showLegacyYativerse } = useAppSelector(
        (state) => state.featureFlags.features,
    );

    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const isRegisteredUser = useAppSelector((state) => state.user.isRegisteredUser);
    const isAdmin = useAppSelector((state) => state.user.userClass?.isAdmin);

    const profilePath = primaryYat ? `${primaryYat}` : 'profile';
    const yatPath = showLegacyYativerse ? DEP_YATIVERSE : '/create';

    return (
        <Wrapper>
            <TopButtons>
                {showDiscoverPage && (
                    <NavButton icon="discover" to={`/discover`} isActive={location.pathname.startsWith('/discover')} />
                )}

                {isAuthenticated ? (
                    <NavPopover
                        button={
                            <NavButton
                                icon="profile"
                                to={`/${profilePath}`}
                                isActive={location.pathname.startsWith(`/${encodeURIComponent(profilePath)}`)}
                            />
                        }
                        allowOverflow
                    >
                        <ProfileSection isOpen={true} isMinimized={true} isStatic={true} />
                    </NavPopover>
                ) : (
                    <NavButton icon="profile" onClick={handleAuthenticate} />
                )}

                {yatFantasyEnabled && (
                    <NavPopover
                        button={
                            <NavButton emoji="🦄" to="/fantasy" isActive={location.pathname.startsWith('/fantasy')} />
                        }
                    >
                        <YatFantasySection isOpen={true} isStatic={true} />
                    </NavPopover>
                )}

                {isAuthenticated && (
                    <NavPopover
                        button={
                            <NavButton
                                emoji="⭐"
                                to="/wishlists"
                                isActive={location.pathname.startsWith('/wishlists')}
                            />
                        }
                    >
                        <WishlistsSection isOpen={true} isStatic={true} />
                    </NavPopover>
                )}

                <NavPopover
                    button={<NavButton emoji="🖖" to={yatPath} isActive={location.pathname.startsWith(yatPath)} />}
                    allowOverflow
                >
                    <YativerseSection isOpen={true} isMinimized={true} isStatic={true} />
                </NavPopover>
            </TopButtons>

            <BottomButtons>
                {isRegisteredUser && (
                    <>
                        <NavPopover button={<NavButton icon="settings" />}>
                            <SettingsSection isOpen={true} />
                        </NavPopover>

                        {isAdmin && (
                            <NavPopover button={<NavButton emoji="🚧" />}>
                                <AdminSection isOpen={true} />
                            </NavPopover>
                        )}
                    </>
                )}
            </BottomButtons>
        </Wrapper>
    );
};
